import { CommonRegistry, InteractionType } from '../components/components';
import { World } from '../World';

export interface InteractionDefinition {
  id: InteractionType;
  label: string;
  priority: number;
  /**
   * Shared predicate to check if the interaction is currently possible.
   */
  canExecute: <Registry extends CommonRegistry>(
    world: World<Registry>,
    sourceId: number,
    targetId: number,
  ) => boolean;
}

export const INTERACTION_REGISTRY: Record<InteractionType, InteractionDefinition> = {
  talk: {
    id: 'talk',
    label: 'Talk',
    priority: 10,
    canExecute: (world, _sourceId, targetId) => {
      const canTalk = world.hasComponent(targetId, 'CanTalk');
      const isDead = world.hasComponent(targetId, 'Dead');
      return canTalk && !isDead;
    },
  },
  loot: {
    id: 'loot',
    label: 'Loot',
    priority: 5,
    canExecute: (world, _sourceId, targetId) => {
      const isDead = world.hasComponent(targetId, 'Dead');
      const hasInventory = world.hasComponent(targetId, 'Inventory');
      return isDead && hasInventory;
    },
  },
  pickpocket: {
    id: 'pickpocket',
    label: 'Pickpocket',
    priority: 20, // Higher priority when sneaking
    canExecute: (world, sourceId, targetId) => {
      const input = world.getComponent(sourceId, 'Input');
      const isDead = world.hasComponent(targetId, 'Dead');
      const hasInventory = world.hasComponent(targetId, 'Inventory');
      const isSneaking = input?.sneak ?? false;
      return !isDead && hasInventory && isSneaking;
    },
  },
  portal: {
    id: 'portal',
    label: 'Use',
    priority: 1,
    canExecute: (world, _sourceId, targetId) => {
      const portal = world.getComponent(targetId, 'Portal');
      return portal?.triggerType === 'door';
    },
  },
  bed: {
    id: 'bed',
    label: 'Use Bed',
    priority: 1,
    canExecute: (world, _sourceId, targetId) => {
      return world.hasComponent(targetId, 'Bed');
    },
  },
};

/**
 * Returns the highest priority valid interaction for a source and target.
 */
export function getPrimaryInteraction<Registry extends CommonRegistry>(
  world: World<Registry>,
  sourceId: number,
  targetId: number,
): InteractionDefinition | null {
  const interactions = Object.values(INTERACTION_REGISTRY)
    .filter((def) => def.canExecute(world, sourceId, targetId))
    .sort((a, b) => b.priority - a.priority);

  return interactions[0] ?? null;
}
